import { defineStore } from 'pinia';
import { PaymentListItem } from '@/models/payment/payment-list-item.model';
import { ModerationActionListItem } from '@/models/payment/moderation-action-list.model';
import { api } from '@/services/http.service';
import { ResponseWithHeaders } from '@/models/common/response-with-header.model';
import { showErrorNotification, showSuccessNotification } from '@/utils/show-notification.util';
import { ModerationStatus } from '@/models/payment/moderation-status.model';
import { PayoutOrderListItem } from '@/models/payout-order/payout-order-list.model';
import {
  ProcessingModeratedTransactionManualFlowPayload,
} from '@/models/payment/processing-moderated-transaction-manual-flow.model';

interface PaymentState {
  paymentList: PaymentListItem[];
  isPaymentListLoading: boolean;
  moderationActionList: ModerationActionListItem[];
  isModerationActionListLoading: boolean;
  isProcessingModeratedTransaction: boolean;
  currentPayoutOrder: PayoutOrderListItem | null;
  isManualDialogVisible: boolean;
}

export const usePaymentStore = defineStore('PaymentStore', {
  state: (): PaymentState => ({
    paymentList: [],
    isPaymentListLoading: false,
    moderationActionList: [],
    isModerationActionListLoading: false,
    isProcessingModeratedTransaction: false,
    currentPayoutOrder: null,
    isManualDialogVisible: false,
  }),
  actions: {
    setPaymentListLoading(isLoading: boolean): void {
      this.isPaymentListLoading = isLoading;
    },
    setModerationActionListLoading(isLoading: boolean): void {
      this.isModerationActionListLoading = isLoading;
    },
    setProcessingModeratedTransaction(isProcessing: boolean): void {
      this.isProcessingModeratedTransaction = isProcessing;
    },
    setCurrentPayoutOrder(payoutOrder: PayoutOrderListItem): void {
      this.currentPayoutOrder = payoutOrder;
    },
    setManualDialogVisible(isVisible: boolean): void {
      this.isManualDialogVisible = isVisible;
    },
    getPaymentList(whitelabelSlug: string, filterList = {}): Promise<PaymentListItem[]> {
      this.setPaymentListLoading(true);

      return api.payment.getPaymentListRequest.perform(whitelabelSlug, { filterList })
        .then(({ data }: ResponseWithHeaders<PaymentListItem[]>) => {
          this.paymentList = data;

          return data;
        })
        .catch((error) => {
          showErrorNotification(error);

          return [];
        })
        .finally(() => {
          this.setPaymentListLoading(false);
        });
    },
    getModerationActionList(whitelabelSlug: string, playerId: string): Promise<ModerationActionListItem[]> {
      if (!this.moderationActionList.length) {
        return Promise.resolve(this.moderationActionList);
      }

      this.setModerationActionListLoading(true);

      return api.payment.getModerationActionListRequest.perform(whitelabelSlug, playerId)
        .then((data: ModerationActionListItem[]) => {
          this.moderationActionList = data;

          return data;
        })
        .catch((error) => {
          showErrorNotification(error);

          return [];
        })
        .finally(() => {
          this.setModerationActionListLoading(false);
        });
    },
    updateProcessingModeratedTransaction(
      whitelabelSlug: string, playerId: string, status: ModerationStatus,
    ): Promise<void> {
      this.setProcessingModeratedTransaction(true);

      return api.payment.updateProcessingModeratedTransactionRequest.perform(whitelabelSlug, playerId, status)
        .then(() => {
          showSuccessNotification('Order is updated');
        })
        .catch((error) => {
          showErrorNotification(error);
        })
        .finally(() => {
          this.setProcessingModeratedTransaction(false);
        });
    },
    updateProcessingModeratedTransactionForceTake(
      whitelabelSlug: string, playerId: string,
    ): Promise<void> {
      this.setProcessingModeratedTransaction(true);

      return api.payment.updateProcessingModeratedTransactionForceTakeRequest.perform(whitelabelSlug, playerId)
        .then(() => {
          showSuccessNotification('Order is updated');
        })
        .catch((error) => {
          showErrorNotification(error);
        })
        .finally(() => {
          this.setProcessingModeratedTransaction(false);
        });
    },
    updateProcessingModeratedTransactionManualFlow(
      payload: ProcessingModeratedTransactionManualFlowPayload,
    ): Promise<void> {
      this.setProcessingModeratedTransaction(true);

      return api.payment.updateProcessingModeratedTransactionManualFlowRequest
        .perform(payload)
        .then(() => {
          showSuccessNotification('Order is updated');
        })
        .catch((error) => {
          showErrorNotification(error);
        })
        .finally(() => {
          this.setProcessingModeratedTransaction(false);
        });
    },
  },
});
